import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes'
import { createApi, fetchBaseQuery, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react'
import { apiPath, baseUrl } from 'config'
import {
  AddressString,
  DepositWithdrawalAssetRequest,
  DepositWithdrawalAssetResponse,
  GetInvestorDataResponse,
  GetInvestorDataResponseWithPoolsLogo,
  GetPoolsPaginationResponse,
  GetPoolsTransformResponse,
  KycStartRequest,
  KycStartResponse,
  KycStatusResponseDTO,
  StatusPool,
  VerificationRequest,
} from 'types'

import { disconnect } from 'store/reducers-common/Auth/slice'
import {
  ClientVerification,
  setClientVerification,
  setDepositWithdrawalAssetData,
} from 'store/reducers-common/Pool/slice'
import { getSignature } from 'utils/getSignature'

export const queryInvestorPools = createApi({
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (builder) => ({
    getInvestorAssetChange: builder.query<DepositWithdrawalAssetResponse, DepositWithdrawalAssetRequest>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const {
          investorAddress,
          limit,
          minExpirationDate,
          order,
          page,
          poolAddress,
          setStore = true,
          status,
          type,
        } = arg

        const statusParam = Array.isArray(status)
          ? status.reduce((acc, curr) => `${acc}&status=${curr}`, '')
          : `&status=${status}`
        let url = `${apiPath.assetsChanges(poolAddress)}?${statusParam}&investorAddress=${investorAddress}&page=${page}&limit=${limit}`
        if (type) {
          url = url.concat(`&type=${type}`)
        }
        if (order) {
          url = url.concat(`&order=${order}`)
        }
        if (minExpirationDate) {
          const isoDate = minExpirationDate.toISOString()
          url = url.concat(`&minExpirationDate=${isoDate}`)
        }

        const result = await baseQuery({
          method: 'GET',
          url,
        })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        const data = result.data as DepositWithdrawalAssetResponse

        data.data.sort((a, b) => new Date(b.updatedAt).valueOf() - new Date(a.updatedAt).valueOf())
        if (setStore) {
          _api.dispatch(setDepositWithdrawalAssetData(data))
        }

        return { data }
      },
    }),
    getInvestorPortfolio: builder.query<GetInvestorDataResponseWithPoolsLogo, AddressString>({
      queryFn: async (investorAddress, _api, _extraOptions, baseQuery) => {
        const url = apiPath.investorsPortfolio(investorAddress)

        try {
          const result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta> = await baseQuery({
            method: 'GET',
            url,
          })

          if (!result.data) {
            return { error: result.error as FetchBaseQueryError }
          }

          const { pools } = result.data as GetInvestorDataResponse

          const poolsWithLogo = pools.map((pool) => {
            if (!pool.logo) {
              return pool
            }
            const objLogo = JSON.parse(pool.logo)
            return { ...pool, logo: objLogo }
          })

          return {
            data: {
              ...(result.data as GetInvestorDataResponse),
              pools: poolsWithLogo,
            } as GetInvestorDataResponseWithPoolsLogo,
          }
        } catch (error) {
          return error
        }
      },
    }),
    getInvestorVerification: builder.query<ClientVerification, VerificationRequest>({
      queryFn: async (body, _api, _extraOptions, baseQuery) => {
        const url = apiPath.clientVerification

        const { deadline, signature } = await getSignature({
          payload: body,
          primaryType: 'GetVerification',
          url,
        })

        if (!signature) {
          _api.dispatch(disconnect())
          return { error: new Error('No signature') as unknown as FetchBaseQueryError }
        }

        const result = await baseQuery({
          body,
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url,
        })

        if (!result.data) {
          _api.dispatch(
            setClientVerification({
              signature: '0x0',
              tier: 0,
              validTill: 0,
            }),
          )
          return { error: result.error as FetchBaseQueryError }
        }

        const verification = result.data as ClientVerification
        _api.dispatch(setClientVerification(verification))

        return { data: verification }
      },
    }),
    getKycStatus: builder.query<KycStatusResponseDTO, void>({
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        const url = apiPath.statusKyc

        const { deadline, signature } = await getSignature({
          primaryType: 'GetKycStatus',
          url,
        })

        if (!signature) {
          _api.dispatch(disconnect())
          return { error: new Error('No signature') as unknown as FetchBaseQueryError }
        }

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          method: 'GET',
          url,
        })

        if (result.error) {
          return { error: result.error }
        }

        const kycStatus = result.data as KycStatusResponseDTO
        return { data: kycStatus }
      },
    }),
    getKycVerification: builder.query<KycStartResponse, KycStartRequest>({
      queryFn: async (body, _api, _extraOptions, baseQuery) => {
        const url = apiPath.startKyc

        const { deadline, signature } = await getSignature({
          payload: { level: body.level },
          primaryType: 'StartKyc',
          url,
        })

        if (!signature) {
          _api.dispatch(disconnect())
          return { error: new Error('No signature') as unknown as FetchBaseQueryError }
        }

        const result = await baseQuery({
          body,
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url,
        })

        if (result.error) {
          return { error: result.error }
        }

        const kycData = result.data as KycStartResponse
        return { data: kycData }
      },
    }),
    getPublicPools: builder.query<GetPoolsTransformResponse, { limit?: number; status?: StatusPool[] }>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const limit = arg ? `&limit=${arg?.limit}` : ''
        const status = arg?.status !== undefined ? `&${arg.status.map((s) => `status=${s}`).join('&')}` : ''
        const url = `${apiPath.pool}?type=public${limit + status}`

        const result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta> = await baseQuery({
          method: 'GET',
          url,
        })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        const { data: pools } = result.data as GetPoolsPaginationResponse

        const transformPools = pools.map((pool) => {
          if (!pool.logo) {
            return pool
          }
          const objLogo = JSON.parse(pool.logo)
          return { ...pool, logo: objLogo }
        })

        return {
          data: { ...(result.data as GetPoolsPaginationResponse), data: transformPools } as GetPoolsTransformResponse,
        }
      },
    }),
  }),
  reducerPath: 'investorPools',
  tagTypes: ['GetInvestorPools'],
})

export const {
  useGetInvestorAssetChangeQuery,
  useGetPublicPoolsQuery,
  useLazyGetInvestorPortfolioQuery,
  useLazyGetInvestorVerificationQuery,
  useLazyGetKycStatusQuery,
  useLazyGetKycVerificationQuery,
} = queryInvestorPools
